import type { SolarProject } from "../types/solar";
import type { OptimizationEffort } from "../lib/Solar/SolarApiClient";
import { FUNNEL_STEP_ID_ORDER, LEGACY_FUNNEL_STEP_IDS, FUNNEL_STEP_IDS } from "../constants/funnelSteps";
import { createLogger } from "../lib/logger.ts";

const logger = createLogger("LocalStorageManager");

/**
 * Normalizes the optimization effort of a stored project.
 * A missing value means the legacy default "fast" mode.
 */
const effortOf = (project: SolarProject): OptimizationEffort => project.optimizationEffort ?? 'fast';

/**
 * Maximum number of projects to keep in localStorage
 * Prevents unlimited growth of usedProjects array
 * Adjust this value to change how many projects are cached locally
 */
export const MAX_SAVED_PROJECTS = 10;

const FUNNEL_DATA = 'currentFunnelData';
const FUNNEL_STEP_COUNT = 'currentFunnelStepCount';
const PROJECT_KEY = 'usedProjects';
const CURRENT_PROJECT = 'currentProject';
const CURRENT_FUNNEL_DATA_VERSION = 2 as const;

type FunnelDataEnvelope = {
    version: typeof CURRENT_FUNNEL_DATA_VERSION;
    stepIndex: number;
    steps: FunnelData;
    updatedAt: string;
};

type FunnelStorageState = {
    stepCount: number;
    funnelData: FunnelData;
};

const funnelStepIds: FunnelStepId[] = FUNNEL_STEP_ID_ORDER;

// Legacy step ID that was renamed to 'solar-configurator'
const LEGACY_STEP_ID_MAP: Record<string, FunnelStepId> = {
    [LEGACY_FUNNEL_STEP_IDS.LAYOUT]: FUNNEL_STEP_IDS.SOLAR_CONFIGURATOR,
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isFunnelDataEnvelope = (value: unknown): value is FunnelDataEnvelope => {
    return isRecord(value)
        && value.version === CURRENT_FUNNEL_DATA_VERSION
        && typeof value.stepIndex === 'number'
        && isRecord(value.steps);
};

const isLegacyFunnelDataEnvelope = (value: unknown): value is { version: 1; steps: FunnelData } => {
    return isRecord(value)
        && value.version === 1
        && isRecord(value.steps);
};

type PickResult = { funnelData: FunnelData; wasMigrated: boolean };

const pickKnownFunnelSteps = (steps: Record<string, unknown>): PickResult => {
    const normalized: FunnelData = {};
    let wasMigrated = false;

    // Migrate legacy step IDs before picking known steps
    const migratedSteps: Record<string, unknown> = { ...steps };
    for (const [legacyId, newId] of Object.entries(LEGACY_STEP_ID_MAP)) {
        if (legacyId in migratedSteps && !(newId in migratedSteps)) {
            migratedSteps[newId] = migratedSteps[legacyId];
            delete migratedSteps[legacyId];
            wasMigrated = true;
        }
    }

    for (const stepId of funnelStepIds) {
        const stepValue = migratedSteps[stepId];
        if (stepValue !== undefined) {
            normalized[stepId] = stepValue as FunnelStepData;
        }
    }

    return { funnelData: normalized, wasMigrated };
};

const persistNormalizedFunnelData = (funnelData: FunnelData): void => {
    persistFunnelStorageState({
        stepCount: getCurrentStepCountFromLocalStorage(),
        funnelData,
    });
};

const persistFunnelStorageState = (state: FunnelStorageState): void => {
    const envelope: FunnelDataEnvelope = {
        version: CURRENT_FUNNEL_DATA_VERSION,
        stepIndex: state.stepCount,
        steps: state.funnelData,
        updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(FUNNEL_DATA, JSON.stringify(envelope));
};

const readStoredFunnelState = (): FunnelStorageState | undefined => {
    const saved = localStorage.getItem(FUNNEL_DATA);
    if (!saved) {
        return undefined;
    }

    const parsed = JSON.parse(saved) as unknown;

    if (isFunnelDataEnvelope(parsed)) {
        const stepCount = Number.isFinite(parsed.stepIndex) && parsed.stepIndex >= 0 ? parsed.stepIndex : 0;
        const { funnelData, wasMigrated } = pickKnownFunnelSteps(parsed.steps);
        const state: FunnelStorageState = { stepCount, funnelData };

        if (wasMigrated) {
            logger.debug('Migrating funnel step IDs in localStorage', { from: parsed.steps, to: funnelData });
            persistFunnelStorageState(state);
        }

        return state;
    }

    if (isLegacyFunnelDataEnvelope(parsed)) {
        const legacyState: FunnelStorageState = {
            stepCount: getCurrentStepCountFromLocalStorage(),
            funnelData: pickKnownFunnelSteps(parsed.steps).funnelData,
        };

        persistFunnelStorageState(legacyState);
        return legacyState;
    }

    if (isRecord(parsed) && !('version' in parsed)) {
        const legacyState: FunnelStorageState = {
            stepCount: getCurrentStepCountFromLocalStorage(),
            funnelData: pickKnownFunnelSteps(parsed).funnelData,
        };

        persistFunnelStorageState(legacyState);
        return legacyState;
    }

    clearFunnelState();
    return undefined;
};

const getStepCountFromStoredEnvelope = (): number | undefined => {
    const saved = localStorage.getItem(FUNNEL_DATA);
    if (!saved) {
        return undefined;
    }

    const parsed = JSON.parse(saved) as unknown;
    if (isFunnelDataEnvelope(parsed)) {
        return Number.isFinite(parsed.stepIndex) && parsed.stepIndex >= 0 ? parsed.stepIndex : 0;
    }

    return undefined;
};

const clearFunnelState = (): void => {
    localStorage.removeItem(FUNNEL_DATA);
    localStorage.removeItem(FUNNEL_STEP_COUNT);
};

export const saveFunnelDataToLocalStorage = (funnelData: FunnelData): void => {
    logger.debug('Saving funnel data to localStorage', { funnelData });
    persistNormalizedFunnelData(funnelData);
};

export const resetFunnelDataInLocalStorage = (softReset: boolean = true): void => {
    logger.debug('Reset funnel data in localStorage');
    if (!softReset) {
        clearFunnelState();
        return;
    }

    saveCurrentStepCountToLocalStorage(0);
};

export const getFunnelDataFromLocalStorage = (): FunnelData | undefined => {
    logger.debug('Reading funnel data from localStorage');
    const stored = readStoredFunnelState();
    if (!stored) {
        return undefined;
    }

    return stored.funnelData;
}

export const getCurrentStepCountFromLocalStorage = (): number => {
    logger.debug('Getting current step count from localStorage');
    const saved = localStorage.getItem(FUNNEL_STEP_COUNT);
    const parsed = saved ? Number(saved) : getStepCountFromStoredEnvelope() ?? 0;
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export const saveCurrentStepCountToLocalStorage = (stepCount: number): void => {
    logger.debug('Saving current step count to localStorage', { stepCount });
    localStorage.setItem(FUNNEL_STEP_COUNT, stepCount.toString());
    const stored = readStoredFunnelState();
    if (stored) {
        persistFunnelStorageState({
            stepCount,
            funnelData: stored.funnelData,
        });
    }
}

export const getFunnelStorageStateFromLocalStorage = (): FunnelStorageState => {
    const stored = readStoredFunnelState();
    if (!stored) {
        return {
            stepCount: 0,
            funnelData: {},
        };
    }

    return stored;
};

export const getProjectFromLocalStorage = (address: AddressWithPrediction, optimizationEffort: OptimizationEffort = 'fast') => {
    if (!address.postalAddress || !address.prediction) {
        return;
    }

    const savedCurrentProject = JSON.parse(localStorage.getItem(CURRENT_PROJECT) || "null") as SolarProject | null;

    if (savedCurrentProject
        && savedCurrentProject.forAddress.prediction?.placeId === address.prediction?.placeId
        && effortOf(savedCurrentProject) === optimizationEffort) {
        logger.debug('Found current project in local storage', { savedCurrentProject });
        return savedCurrentProject;
    }

    const saved = JSON.parse(localStorage.getItem(PROJECT_KEY) || "[]") as SolarProject[];
    return saved.find((item) => {
        if (item.forAddress.prediction?.placeId === address.prediction?.placeId
            && effortOf(item) === optimizationEffort) {
            logger.debug('Found saved project', { item });
            return true;
        }
    });
};
//
// const slimProjectData = (fullData: ProjectGetDataResponse): SlimmedProjectGetDataResponse => {
//     const slimmed: SlimmedProjectGetDataResponse = {
//         address: fullData.address,
//         buildingType: fullData.buildingType,
//         countryCode: fullData.countryCode,
//         // customMapHandlerData: fullData.customMapHandlerData,
//         // customOfferVariables: fullData.customOfferVariables,
//         id: fullData.id,
//         locale: fullData.locale,
//         optimizerError: fullData.optimizerError,
//         rooftopError: fullData.rooftopError,
//         rooftopInputData: fullData.rooftopInputData,
//         setupData: fullData.setupData,
//         type: fullData.type,
//     };
//
//     // Slim down confirmedRooftopData - keep IDs only
//     if (fullData.confirmedRooftopData) {
//         slimmed.confirmedRooftopData = {
//             confirmedAt: fullData.confirmedRooftopData.confirmedAt,
//             countryCode: fullData.confirmedRooftopData.countryCode,
//             referenceId: fullData.confirmedRooftopData.referenceId,
//             rooftopRegion: fullData.confirmedRooftopData.rooftopRegion,
//             recommendedEpsg: fullData.confirmedRooftopData.recommendedEpsg,
//             houseOrientationFromHorizontalAxisRadians: fullData.confirmedRooftopData.houseOrientationFromHorizontalAxisRadians,
//             // Extract panel installation area IDs
//             panelInstallationAreaIds: fullData.confirmedRooftopData.panelInstallationAreas?.map(area => area.properties.id),
//         };
//     }
//
//     // Slim down optimizerResult - remove images and heavy calculations
//     if (fullData.optimizerResult) {
//         // Create slimmed version for each optimization type
//         const slimmedResult: Record<string, SlimmedOptimizerResult> = {};
//
//         for (const [optimizationType, result] of Object.entries(fullData.optimizerResult)) {
//             if (result) {
//                 slimmedResult[optimizationType] = {
//                     pvPlant: result.pvPlant ? {
//                         panelId: result.pvPlant.panelId,
//                         totalNumberOfPanels: result.pvPlant.totalNumberOfPanels,
//                         totalPanelsRooftopArea: result.pvPlant.totalPanelsRooftopArea,
//                         maxNumberOfPanelsOnRooftop: result.pvPlant.maxNumberOfPanelsOnRooftop,
//                     } : undefined,
//                     inverter: result.inverter,
//                     optimizationType: result.optimizationType,
//                     keyEconomicIndicators: result.keyEconomicIndicators,
//                     keyTechnicalParameters: {
//                         pvCapacity: result.keyTechnicalParameters.pvCapacity,
//                         co2Reduction: result.keyTechnicalParameters.co2Reduction,
//                         batteryCapacity: result.keyTechnicalParameters.batteryCapacity,
//                         inverterCapacity: result.keyTechnicalParameters.inverterCapacity,
//                         selfSufficiencyYearly: result.keyTechnicalParameters.selfSufficiencyYearly,
//                         yearlySolarPlantProduction: result.keyTechnicalParameters.yearlySolarPlantProduction,
//                         co2ReductionAbsolute: result.keyTechnicalParameters.co2ReductionAbsolute,
//                     },
//                     investmentCostBreakdown: result.investmentCostBreakdown,
//                     packagesOutput: result.packagesOutput ? {
//                         optimalPackageId: result.packagesOutput.optimalPackageId,
//                         optimalPackageName: result.packagesOutput.optimalPackageName,
//                     } : undefined,
//                     inverters: result.inverters,
//                     batteries: result.batteries,
//                     // Removed: hourlyElectricityFlow, hourlyBatteryOperation, encodedImageWithPanels, monthlyBalanceSummary
//                 } as SlimmedOptimizerResult;
//             }
//         }
//         slimmed.optimizerResult = slimmedResult;
//     }
//
//     return slimmed;
// };

export const saveProjectToLocalStorage = (project: SolarProject) => {
    const saved = JSON.parse(localStorage.getItem(PROJECT_KEY) || "[]") as SolarProject[];

    // Check if project already exists (same address AND same optimization mode)
    const projectExists = saved.find((item) => {
        if (item.forAddress.prediction?.placeId === project.forAddress.prediction?.placeId
            && effortOf(item) === effortOf(project)) {
            return true;
        }
    });

    if (projectExists) {
        return;
    }

    logger.debug('Saving project', { project });
    saved.push({
        projectId: project.projectId,
        forAddress: project.forAddress,
        optimizationEffort: project.optimizationEffort
    } as SolarProject);
    // Keep only the last MAX_SAVED_PROJECTS
    if (saved.length > MAX_SAVED_PROJECTS) {
        logger.debug('Project count exceeds MAX_SAVED_PROJECTS, removing oldest projects', {
            count: saved.length,
            max: MAX_SAVED_PROJECTS
        });
        saved.splice(0, 1);
    }

    localStorage.setItem(PROJECT_KEY, JSON.stringify(saved));
};

export const saveCurrentProjectToLocalStorage = (project: SolarProject): void => {
    localStorage.setItem(CURRENT_PROJECT, JSON.stringify(project));
}
